import { AccordionDetails, CSSObject, Stack, styled, Theme } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import React from 'react';

import CloseIcon from '@/assets/icons/close-button.svg?react';
import ExpandIcon from '@/assets/icons/dropdown-arrow.svg?react';
import { TransactionDetailsEntry } from '@/components/molecules/TransactionDetailsEntry';
import { HIGH_PRECISION_DECIMAL_PLACES } from '@/constants';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { Simulation } from '@/hooks/useSimulation';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';
import { FeeType, SpreadFee } from '@/types/transaction';

const QUICK_SELECT_BPS = [10, 25, 50, 75] as const;

const MAJOR_CURRENCIES = [CurrencyCodeType.USD, CurrencyCodeType.USDC, CurrencyCodeType.USDT];

const RATE_FORMATTER_CONFIG = {
  maximumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
  minimumFractionDigits: HIGH_PRECISION_DECIMAL_PLACES,
};

const PERCENTAGE_FORMATTER_CONFIG = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
};

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onApply: (markupBps: number) => void;
  readonly onBpsChange: (markupBps: number | null) => void;
  readonly currentMarkupBps: number | null;
  readonly simulation: Simulation;
  readonly fromCurrency: CurrencyCodeType;
  readonly toCurrency: CurrencyCodeType;
  readonly isLoading: boolean;
}

const formatRateDisplay = (
  rate: number | undefined | null,
  fromCurrency: CurrencyCodeType,
  toCurrency: CurrencyCodeType,
  rateFormatter: Intl.NumberFormat,
): string => {
  if (!rate || rate === 0 || Number.isNaN(rate)) {
    return `1 ${fromCurrency} = 1 ${toCurrency}`;
  }

  if (MAJOR_CURRENCIES.includes(toCurrency)) {
    const invertedRate = 1 / rate;
    return `1 ${toCurrency} = ${rateFormatter.format(invertedRate)} ${fromCurrency}`;
  }

  return `1 ${fromCurrency} = ${rateFormatter.format(rate)} ${toCurrency}`;
};

const Loading: React.FC = (): React.ReactElement => (
  <Stack sx={styles.loadingContainer}>
    <CircularProgress size={24} />
  </Stack>
);

export const FxMarkupModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    open,
    onClose,
    onApply,
    onBpsChange,
    currentMarkupBps,
    simulation,
    fromCurrency,
    toCurrency,
    isLoading,
  } = props;

  const tr = useTranslator();
  const formatter = useCurrencyFormatter();
  const rateFormatter = useLocalizedFormatter(RATE_FORMATTER_CONFIG);
  const percentageFormatter = useLocalizedFormatter(PERCENTAGE_FORMATTER_CONFIG);

  const [bpsInput, setBpsInput] = React.useState<string>('');

  React.useEffect((): void => {
    if (open) {
      setBpsInput(currentMarkupBps ? String(currentMarkupBps) : '');
    }
  }, [open, currentMarkupBps]);

  const bpsValue = React.useMemo((): number => {
    const parsed = parseInt(bpsInput, 10);

    return Number.isNaN(parsed) ? 0 : parsed;
  }, [bpsInput]);

  const percentageDisplay = React.useMemo((): string => {
    return `= ${percentageFormatter.format(bpsValue / 100)}%`;
  }, [bpsValue, percentageFormatter]);

  const handleBpsChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value.replace(/[^0-9]/g, '');
      setBpsInput(value);
      const parsed = parseInt(value, 10);
      onBpsChange(Number.isNaN(parsed) || parsed === 0 ? null : parsed);
    },
    [onBpsChange],
  );

  const handleChipClick = React.useCallback(
    (bps: number): void => {
      setBpsInput(String(bps));
      onBpsChange(bps);
    },
    [onBpsChange],
  );

  const handleApply = React.useCallback((): void => {
    onApply(bpsValue);
  }, [bpsValue, onApply]);

  const conversionDetails = simulation.transactionDetails.conversionDetails;
  const feeDetails = simulation.transactionDetails.feeDetails;

  const integratorSpreadFee = React.useMemo((): SpreadFee | undefined => {
    return feeDetails.spreadFees.find(
      (fee: SpreadFee): boolean => fee.type === FeeType.INTEGRATOR_SPREAD,
    );
  }, [feeDetails.spreadFees]);

  const youEarn = React.useMemo((): string => {
    if (!integratorSpreadFee) {
      return formatter.format(0, CurrencyCodeType.USD);
    }

    return formatter.format(
      parseFloat(integratorSpreadFee.calculatedFee),
      integratorSpreadFee.feeCurrency,
    );
  }, [integratorSpreadFee, formatter]);

  const customerPays = React.useMemo((): string => {
    return formatter.format(simulation.from.value, fromCurrency);
  }, [simulation.from.value, fromCurrency, formatter]);

  const recipientGets = React.useMemo((): string => {
    return formatter.format(simulation.to.value, toCurrency);
  }, [simulation.to.value, toCurrency, formatter]);

  const midMarketRate = React.useMemo((): string => {
    return formatRateDisplay(
      conversionDetails?.marketExchangeRate,
      fromCurrency,
      toCurrency,
      rateFormatter,
    );
  }, [conversionDetails?.marketExchangeRate, fromCurrency, toCurrency, rateFormatter]);

  const calizaSpreadPercentage = React.useMemo((): string => {
    if (!conversionDetails?.calizaRate || conversionDetails.calizaRate === 0) {
      return '0.00%';
    }

    return `${percentageFormatter.format(conversionDetails.calizaRate)}%`;
  }, [conversionDetails?.calizaRate, percentageFormatter]);

  const clientRateDisplay = React.useMemo((): string => {
    return formatRateDisplay(
      conversionDetails?.clientRate,
      fromCurrency,
      toCurrency,
      rateFormatter,
    );
  }, [conversionDetails?.clientRate, fromCurrency, toCurrency, rateFormatter]);

  const integratorRateDisplay = React.useMemo((): string => {
    return formatRateDisplay(
      conversionDetails?.integratorRate,
      fromCurrency,
      toCurrency,
      rateFormatter,
    );
  }, [conversionDetails?.integratorRate, fromCurrency, toCurrency, rateFormatter]);

  const yourSpreadPercentage = React.useMemo((): string => {
    return `${percentageFormatter.format(bpsValue / 100)}%`;
  }, [bpsValue, percentageFormatter]);

  const totalSpreadPercentage = React.useMemo((): string => {
    const calizaRate = Number(conversionDetails?.calizaRate) || 0;
    const totalSpread = calizaRate + bpsValue / 100;
    return `${percentageFormatter.format(totalSpread)}%`;
  }, [conversionDetails?.calizaRate, bpsValue, percentageFormatter]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent sx={styles.dialogContent}>
        <Stack sx={styles.header}>
          <Typography variant="h6">{tr('FX_MARKUP_MODAL_TITLE')}</Typography>
          <IconButton onClick={onClose} color="inherit">
            <CloseIcon fill="currentColor" />
          </IconButton>
        </Stack>
        <Typography sx={styles.subtitle}>{tr('FX_MARKUP_MODAL_SUBTITLE')}</Typography>

        <TextField
          label={tr('FX_MARKUP_BPS_LABEL')}
          value={bpsInput}
          onChange={handleBpsChange}
          fullWidth
          slotProps={{
            htmlInput: { inputMode: 'numeric' },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography sx={styles.percentageAdornment}>{percentageDisplay}</Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={styles.bpsInput}
        />

        <ButtonGroup color="inherit" size="compact" fullWidth sx={styles.quickSelect}>
          {QUICK_SELECT_BPS.map(
            (bps: number): React.ReactElement => (
              <Button
                key={bps}
                variant={bpsValue === bps ? 'dark' : 'outlined'}
                onClick={(): void => handleChipClick(bps)}>
                {`${bps} bps`}
              </Button>
            ),
          )}
        </ButtonGroup>
        <Typography variant="caption" sx={styles.bpsHint}>
          {tr('FX_MARKUP_BPS_HINT')}
        </Typography>

        <Stack sx={styles.previewCard}>
          <Typography variant="h7" sx={styles.previewTitle}>
            {tr('FX_MARKUP_CUSTOMER_QUOTE_PREVIEW')}
          </Typography>

          {isLoading ? (
            <Loading />
          ) : (
            <Stack sx={styles.detailsList}>
              <TransactionDetailsEntry
                label={tr('FX_MARKUP_YOU_EARN')}
                value={<Typography color="primary.main">{youEarn}</Typography>}
              />
              <TransactionDetailsEntry label={tr('FX_MARKUP_CUSTOMER_PAYS')} value={customerPays} />
              <Stack sx={styles.recipientGetsRow}>
                <Stack sx={styles.recipientGetsLabel}>
                  <Typography color="custom.LightGray">{tr('RECIPIENT_GETS')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({tr('FX_MARKUP_FIXED_BASED_ON_INPUT')})
                  </Typography>
                </Stack>
                <Typography>{recipientGets}</Typography>
              </Stack>
            </Stack>
          )}
        </Stack>

        <StyledAccordion>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="h7">{tr('FX_MARKUP_RATE_BREAKDOWN')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {isLoading ? (
              <Loading />
            ) : (
              <Stack sx={styles.detailsList}>
                <TransactionDetailsEntry
                  label={`${tr('MID_MARKET_RATE')} (${tr('MID_MARKET_RATE_SOURCE')})`}
                  value={midMarketRate}
                />
                <TransactionDetailsEntry label={tr('CALIZA_RATE')} value={clientRateDisplay} />
                <Typography variant="caption" sx={styles.spreadCaption}>
                  {tr('FX_MARKUP_OUR_SPREAD', calizaSpreadPercentage)}
                </Typography>
                {conversionDetails?.integratorRate && (
                  <>
                    <Divider sx={styles.divider} />
                    <TransactionDetailsEntry
                      label={
                        <Typography color="primary.main">
                          {tr('FX_MARKUP_CUSTOMER_RATE')}
                        </Typography>
                      }
                      value={<Typography color="primary.main">{integratorRateDisplay}</Typography>}
                    />
                    <Stack sx={styles.spreadRow}>
                      <Typography variant="caption" color="text.secondary">
                        {tr('FX_MARKUP_YOUR_SPREAD', yourSpreadPercentage)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tr('FX_MARKUP_TOTAL_SPREAD', totalSpreadPercentage)}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            )}
          </AccordionDetails>
        </StyledAccordion>

        <Button
          variant="dark"
          fullWidth
          onClick={handleApply}
          disabled={bpsValue === 0 || isLoading}
          sx={styles.applyButton}>
          {tr('FX_MARKUP_APPLY')}
        </Button>
        <Stack sx={styles.cancelContainer}>
          <Button color="inherit" onClick={onClose}>
            {tr('CANCEL')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const StyledAccordion = styled(MuiAccordion)(
  ({ theme }: { theme: Theme }): CSSObject => ({
    border: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.custom.DividerLight,
    borderRadius: 16,
    '&::before': {
      display: 'none',
    },
    '&:last-of-type': {
      borderRadius: 16,
    },
  }),
);

const styles: Record<string, SystemStyleObject> = {
  dialogContent: {
    py: 4,
    px: 4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subtitle: {
    color: 'custom.LightGray',
    mb: 3,
  },
  bpsInput: {
    mb: 2,
  },
  percentageAdornment: {
    color: 'custom.LightGray',
    whiteSpace: 'nowrap',
  },
  quickSelect: {
    mb: 1,
  },
  bpsHint: {
    color: 'text.secondary',
    mb: 3,
    display: 'block',
  },
  previewCard: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'custom.DividerLight',
    borderRadius: 4,
    p: 3,
    mb: 2,
  },
  previewTitle: {
    mb: 4,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    py: 3,
  },
  detailsList: {
    gap: 1,
  },
  recipientGetsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 24,
  },
  recipientGetsLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 0.5,
  },
  divider: {
    my: 0.5,
  },
  spreadCaption: {
    color: 'text.secondary',
    mt: -1,
  },
  spreadRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    mt: -1,
  },
  applyButton: {
    mt: 3,
  },
  cancelContainer: {
    alignItems: 'center',
    mt: 1,
  },
};
